import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import { Link, useNavigate } from 'react-router-dom';
import EmptyState from './components/EmptyState';
import ErrorState from '../../../components/base/ErrorState';
import AuthRequired from '../../../components/common/AuthRequired';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseAnonKey);

interface Order {
  id: number;
  user_id: string;
  destination_country: string;
  destination_city: string;
  total_cartons: number;
  total_amount: number;
  status: string;
  created_at: string;
  updated_at: string;
}

interface OrderItem {
  id: number;
  order_id: number;
  product_id: number;
  quantity_cartons: number;
  quantity_pairs: number;
  unit_price: number;
  line_total: number;
}

interface Product {
  id: number;
  title: string;
}

interface OrderWithDetails extends Order {
  itemsPreview?: string;
  itemCount?: number;
}

const MyOrdersPage = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<OrderWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [authChecked, setAuthChecked] = useState(false);

  // Filter states
  const [searchId, setSearchId] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [destinationFilter, setDestinationFilter] = useState('all');
  const [destinations, setDestinations] = useState<string[]>([]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (currentUser) {
      loadOrders();
    }
  }, [currentUser, searchId, statusFilter, dateRange, destinationFilter, currentPage]);

  const checkAuth = async () => {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    
    setAuthChecked(true);
    
    if (authError || !user) {
      setLoading(false);
      return;
    }

    setCurrentUser(user);
  };

  const loadOrders = async () => {
    if (!currentUser) return;

    setLoading(true);
    setError(null);

    try {
      // Build query
      let query = supabase
        .from('orders')
        .select('*', { count: 'exact' })
        .eq('user_id', currentUser.id);

      // Apply filters
      if (searchId) {
        query = query.eq('id', parseInt(searchId));
      }

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      if (dateRange !== 'all') {
        const now = new Date();
        let startDate = new Date();

        switch (dateRange) {
          case '7days':
            startDate.setDate(now.getDate() - 7);
            break;
          case '30days':
            startDate.setDate(now.getDate() - 30);
            break;
          case '6months':
            startDate.setMonth(now.getMonth() - 6);
            break;
        }

        if (dateRange !== 'all') {
          query = query.gte('created_at', startDate.toISOString());
        }
      }

      if (destinationFilter !== 'all') {
        const [country, city] = destinationFilter.split('|');
        query = query.eq('destination_country', country);
        if (city) {
          query = query.eq('destination_city', city);
        }
      }

      // Order by created_at descending (newest first)
      query = query.order('created_at', { ascending: false });

      // Pagination
      const offset = (currentPage - 1) * pageSize;
      query = query.range(offset, offset + pageSize - 1);

      const { data: ordersData, error: ordersError, count } = await query;

      if (ordersError) throw ordersError;

      const safeOrders = Array.isArray(ordersData) ? ordersData : [];

      if (safeOrders.length === 0) {
        setOrders([]);
        setLoading(false);
        return;
      }

      // Load order items preview for each order
      const orderIds = safeOrders.map((o: Order) => o.id);
      const { data: itemsData, error: itemsError } = await supabase
        .from('order_items')
        .select('order_id, product_id')
        .in('order_id', orderIds);

      if (itemsError) throw itemsError;

      const safeItems = Array.isArray(itemsData) ? itemsData : [];

      // Get unique product IDs
      const productIds = [...new Set(safeItems.map((item: OrderItem) => item.product_id))];

      // Load product titles
      const { data: productsData, error: productsError } = await supabase
        .from('products')
        .select('id, title')
        .in('id', productIds);

      if (productsError) throw productsError;

      const safeProducts = Array.isArray(productsData) ? productsData : [];

      // Create product map
      const productMap: Record<number, Product> = {};
      safeProducts.forEach((p: Product) => {
        productMap[p.id] = p;
      });

      // Aggregate items by order
      const itemsByOrder: Record<number, OrderItem[]> = {};
      safeItems.forEach((item: OrderItem) => {
        if (!itemsByOrder[item.order_id]) {
          itemsByOrder[item.order_id] = [];
        }
        itemsByOrder[item.order_id].push(item);
      });

      // Enhance orders with preview
      const enhancedOrders: OrderWithDetails[] = safeOrders.map((order: Order) => {
        const items = itemsByOrder[order.id] || [];
        const itemCount = items.length;
        
        let preview = '';
        if (itemCount > 0) {
          const firstProduct = productMap[items[0].product_id];
          preview = firstProduct ? firstProduct.title : 'Product';
          if (itemCount > 1) {
            preview += ` and ${itemCount - 1} more`;
          }
        }

        return {
          ...order,
          itemsPreview: preview,
          itemCount,
        };
      });

      setOrders(enhancedOrders);

      // Load unique destinations for filter
      if (destinations.length === 0) {
        const { data: allOrders, error: allOrdersError } = await supabase
          .from('orders')
          .select('destination_country, destination_city')
          .eq('user_id', currentUser.id);

        if (allOrdersError) throw allOrdersError;

        const safeAllOrders = Array.isArray(allOrders) ? allOrders : [];
        const uniqueDestinations = new Set<string>();
        safeAllOrders.forEach((o: Order) => {
          uniqueDestinations.add(`${o.destination_country}|${o.destination_city}`);
        });

        setDestinations(Array.from(uniqueDestinations));
      }

    } catch (err: any) {
      console.error('Error loading orders:', err);
      setError(err.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'shipped':
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const clearFilters = () => {
    setSearchId('');
    setStatusFilter('all');
    setDateRange('all');
    setDestinationFilter('all');
    setCurrentPage(1);
  };

  const hasActiveFilters = searchId || statusFilter !== 'all' || dateRange !== 'all' || destinationFilter !== 'all';

  if (!authChecked || loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <i className="ri-loader-4-line text-4xl text-teal-600 animate-spin"></i>
              <p className="mt-4 text-gray-600">Loading your orders...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-gray-50 pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AuthRequired />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ErrorState onRetry={loadOrders} />
        </div>
      </div>
    );
  }

  if (orders.length === 0 && !hasActiveFilters) {
    return (
      <div className="min-h-screen bg-gray-50 pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <EmptyState type="no-orders" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">My Orders</h1>
          <p className="text-gray-600">
            Review your past and current wholesale orders. All payments are handled offline.
          </p>
        </div>

        {/* Offline Payment Notice */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <i className="ri-information-line text-blue-600 text-xl mr-3 mt-0.5"></i>
            <div>
              <p className="text-sm text-blue-800">
                <strong>Reminder:</strong> This website does not support online payment. After placing an order, 
                please contact us to complete offline payment. Our team will manually mark your order as paid.
              </p>
            </div>
          </div>
        </div>

        {/* Filter & Search Bar */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search by Order ID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search by Order ID
              </label>
              <input
                type="text"
                placeholder="Enter order ID"
                value={searchId}
                onChange={(e) => {
                  setSearchId(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              />
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm pr-8"
              >
                <option value="all">All statuses</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="paid">Paid</option>
                <option value="shipped">Shipped</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            {/* Date Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date Range
              </label>
              <select
                value={dateRange}
                onChange={(e) => {
                  setDateRange(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm pr-8"
              >
                <option value="all">All time</option>
                <option value="7days">Last 7 days</option>
                <option value="30days">Last 30 days</option>
                <option value="6months">Last 6 months</option>
              </select>
            </div>

            {/* Destination Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Destination
              </label>
              <select
                value={destinationFilter}
                onChange={(e) => {
                  setDestinationFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm pr-8"
              >
                <option value="all">All destinations</option>
                {destinations.map((dest) => {
                  const [country, city] = dest.split('|');
                  return (
                    <option key={dest} value={dest}>
                      {country}, {city}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <div className="mt-4 flex justify-end">
              <button
                onClick={clearFilters}
                className="text-sm text-teal-600 hover:text-teal-700 font-medium flex items-center whitespace-nowrap"
              >
                <i className="ri-close-circle-line mr-1"></i>
                Clear filters
              </button>
            </div>
          )}
        </div>

        {/* Orders List */}
        {orders.length === 0 ? (
          <EmptyState type="no-results" onClearFilters={clearFilters} />
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <div
                key={order.id}
                className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                  {/* Left: Basic Info */}
                  <div className="flex-1 mb-4 lg:mb-0">
                    <div className="flex items-center mb-2">
                      <h3 className="text-lg font-semibold text-gray-900 mr-3">
                        Order #{order.id}
                      </h3>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(
                          order.status
                        )}`}
                      >
                        {getStatusLabel(order.status)}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p>
                        <i className="ri-calendar-line mr-2"></i>
                        {formatDate(order.created_at)}
                      </p>
                      <p>
                        <i className="ri-map-pin-line mr-2"></i>
                        {order.destination_country}, {order.destination_city}
                      </p>
                      {order.itemsPreview && (
                        <p className="text-gray-500">
                          <i className="ri-shopping-bag-line mr-2"></i>
                          {order.itemCount} {order.itemCount === 1 ? 'item' : 'items'}, including {order.itemsPreview}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Middle: Summary */}
                  <div className="flex-1 mb-4 lg:mb-0 lg:mx-8">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">Total Cartons</p>
                        <p className="text-lg font-semibold text-gray-900">
                          {order.total_cartons}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Total Amount</p>
                        <p className="text-lg font-semibold text-gray-900">
                          ${order.total_amount.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Right: Actions */}
                  <div className="flex items-center space-x-3">
                    <Link
                      to={`/account/orders/${order.id}`}
                      className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium text-sm whitespace-nowrap flex items-center"
                    >
                      View Details
                      <i className="ri-arrow-right-line ml-2"></i>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {orders.length > 0 && (
          <div className="mt-8 flex justify-center items-center space-x-4">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium whitespace-nowrap"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              Previous
            </button>
            <span className="text-sm text-gray-600">
              Page {currentPage}
            </span>
            <button
              onClick={() => setCurrentPage((prev) => prev + 1)}
              disabled={orders.length < pageSize}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium whitespace-nowrap"
            >
              Next
              <i className="ri-arrow-right-line ml-2"></i>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyOrdersPage;
